import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { RescheduleAppointmentSchema, Input } from './schema'
import { useRescheduleAppointmentForm } from '.'
import { appointmentShowOptions, appointmentMetadataOptions } from '$api-client/@tanstack/react-query.gen'
import { Text, Button, UncontrolledInput, DurationPicker } from '$components'
import { useEffect, useState } from 'react'
import { TimeFormatter } from '$components'
import { PriceFormatter } from '$components'

export function AppointmentMetadata() {
  const { setValue, getValues } = useFormContext<RescheduleAppointmentSchema>()
  const { defaultValues } = useRescheduleAppointmentForm()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [duration, setDuration] = useState<number>(0)
  const [price, setPrice] = useState<number>(0)

  const { data: appointment } = useQuery({
    ...appointmentShowOptions({
      path: {
        id: defaultValues![Input.AppointmentId]!,
      },
    }),
  })

  const { data: metadata } = useQuery({
    ...appointmentMetadataOptions({
      query: {
        pet_id: appointment?.data.pet.id,
        services: getValues(Input.Services)?.map(s => s.id) ?? [],
      },
    }),
    enabled: !!(appointment?.data?.pet?.id && getValues(Input.Services)?.length),
  })

  // Update values when metadata changes (like create appointment)
  useEffect(() => {
    if (metadata) {
      const { duration, price_in_cents } = metadata.data

      setValue(Input.Duration, Number(duration))
      setValue(Input.Price, Number(price_in_cents))
    }
  }, [metadata, setValue])

  // Reset editing state when services change (like create appointment)
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
    }
  }, [getValues(Input.Services)])

  const onSave = () => {
    if (!isEditing) {
      setDuration(getValues(Input.Duration) || 30)
      setPrice((getValues(Input.Price) || 0) / 100)
      setIsEditing(true)
      return
    }

    const currentDuration = getValues(Input.Duration)
    const currentSlot = getValues(Input.Slot)

    setValue(Input.Duration, duration)
    setValue(Input.Price, price * 100)

    // Clear selected slot if duration changed, as it's no longer valid
    if (currentDuration !== duration && currentSlot) {
      setValue(Input.Slot, null!)
    }

    setIsEditing(false)
  }

  if (!appointment?.data) {
    return null
  }

  return (
    <div className="flex gap-stack-section">
      <div className="flex flex-col gap-stack-tight">
        <Text
          type="s"
          weight="medium"
        >
          Appointment Duration
        </Text>

        {isEditing
          ? (
            <DurationPicker
              duration={duration}
              onChange={setDuration}
              width="fit"
            />
          )
          : (
            <Text
              type="h2"
              weight="normal"
            >
              <TimeFormatter>
                {getValues(Input.Duration) || 30}
              </TimeFormatter>
            </Text>
          )}
      </div>

      <div className="flex flex-col gap-stack-tight">
        <Text
          type="s"
          weight="medium"
        >
          Appointment Price
        </Text>

        {isEditing
          ? (
            <UncontrolledInput
              name={Input.Price}
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.valueAsNumber)}
            />
          )
          : (
            <Text
              type="h2"
              weight="normal"
            >
              <PriceFormatter currency={metadata?.data?.currency || appointment.data.currency}>
                {getValues(Input.Price) || appointment.data.price_in_cents}
              </PriceFormatter>
            </Text>
          )}
      </div>

      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={onSave}
        className="ml-auto"
      >
        {isEditing ? 'Save' : 'Edit'}
      </Button>
    </div>
  )
}
